import { cleanFileName } from "../../filing/utils/emailFile";

// What a document that did not come from Jylo is called once it is saved there, and what
// its check out says about where it was taken. Pure, so both can be tested without Office.

/**
 * The name the open document already has, without its extension, or "" for a document
 * that has never been saved.
 *
 * `url` is `Office.context.document.url`: a local path on the desktop, an https URL for
 * a file in OneDrive or SharePoint (percent-encoded, sometimes with a query string), and
 * empty for a new document. Its extension is dropped whatever it is, because what is
 * uploaded is the host's own format -- a .doc opened in Word is sent as .docx.
 */
export function defaultDocumentName(url: string | null | undefined): string {
  if (!url) return "";

  const path = url.split(/[?#]/)[0];
  const last = path.split(/[\\/]/).pop() ?? "";
  let name = last;
  try {
    name = decodeURIComponent(last);
  } catch {
    // A stray % in a local file name is not an encoding; keep it as typed.
  }

  return cleanFileName(name.replace(/\.[^.]+$/, ""));
}

/**
 * The file name to upload as, or null when the user has left nothing usable.
 *
 * The extension is always the host's, appended here. One the user typed is dropped
 * first, so "Lease.docx" does not become "Lease.docx.docx".
 */
export function uploadFileName(name: string, extension: string): string | null {
  const withoutOwn = name.trim().replace(new RegExp(`\\.${extension}$`, "i"), "");
  const cleaned = cleanFileName(withoutOwn);
  return cleaned ? `${cleaned}.${extension}` : null;
}

const PLATFORM_NAMES: Record<string, string> = {
  PC: "Windows",
  Mac: "Mac",
  OfficeOnline: "the web",
  iOS: "iPad",
  Android: "Android",
};

/**
 * "Word on Windows": recorded on the draft so the holder can later answer "where are my
 * edits". Coarse on purpose -- the API asks for a label, never a fingerprint.
 *
 * `platform` is `Office.context.platform`, passed in as a string so this needs no Office
 * global (its values are those enum members' names).
 */
export function checkOutDeviceLabel(hostDisplayName: string, platform: string | undefined): string {
  const where = platform ? PLATFORM_NAMES[platform] : undefined;
  return where ? `${hostDisplayName} on ${where}` : hostDisplayName;
}
